import { FC } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams } from 'react-router-dom';
import { useKnowledgeNode } from '@/entities/knowledge';
import { KnowledgeForm } from './KnowledgeForm';
import { KnowledgeNodeSets } from './KnowledgeNodeSets';
import { KnowledgeRelations } from './KnowledgeRelations';
import { useKnowledgeForm } from './useKnowledgeForm';

export const KnowledgeCard: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('knowledge');
  const isCreate = id === 'create';

  const { data: node, isLoading: isLoadingNode } = useKnowledgeNode(isCreate ? undefined : id);

  const { form, isLoading, isDeleting, isDisabled, onSubmit, handleTitleSave, handleDelete } = useKnowledgeForm({
    node,
    isCreate,
  });

  const knowledgeSets = node?.knowledgeSets ?? [];
  const hasKnowledgeSets = knowledgeSets.length > 0;

  const relationsSlot = (
    <>
      <KnowledgeRelations node={node} isLoading={isLoadingNode} />
      {!isCreate && node?.id && !isDisabled && <KnowledgeNodeSets nodeId={node.id} knowledgeSets={knowledgeSets} />}
      {!isCreate && isDisabled && hasKnowledgeSets && (
        <div className="flex flex-col gap-2">
          <h3 className="text-base font-semibold text-foreground">{t('knowledgeSets')}</h3>
          <div className="flex flex-col gap-1">
            {knowledgeSets.map((ks) => (
              <NavLink key={ks.id} to={`/knowledge-sets/${ks.id}`} className="text-primary hover:underline text-sm">
                {ks.name}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <FormProvider {...form}>
      <KnowledgeForm
        key={`${node?.id}-${isCreate}`}
        nodeId={node?.id}
        isCreate={isCreate}
        isLoadingData={isLoadingNode}
        isLoading={isLoading}
        isDeleting={isDeleting}
        isDeleted={node?.isDeleted}
        nodeType={node?.type}
        onSave={onSubmit}
        onDelete={handleDelete}
        onTitleSave={handleTitleSave}
        relationsSlot={relationsSlot}
      />
    </FormProvider>
  );
};
